import { loadUsetifulScript, setUsetifulTags } from 'usetiful-sdk';

/**
 * User properties that can be sent to the product tour service for user identification.
 * Common properties include email and name, but additional custom properties can be added.
 */
export interface UserProperties {
  /** User's email address */
  email?: string;
  /** User's display name */
  name?: string;
  /** Allow additional custom properties */
  [key: string]: unknown;
}

/**
 * Tags that can be added to the product tour for user segmentation and targeting.
 * Examples: environment, user actions, etc.
 */
export interface ProductTourTags {
  [key: string]: unknown;
}

// Product tour service interface - allows for easy swapping of implementations
export interface IProductTourService {
  initialize(token: string, debug?: boolean): void;
  identifyUser(userId: string, userProperties?: UserProperties): void;
  addTags(tags: ProductTourTags): void;
  isInitialized(): boolean;
}

// Usetiful SDK wrapper implementation
class UsetifulProductTourService implements IProductTourService {
  private initialized = false;
  private debug = false;

  private log(message: string, data?: unknown): void {
    if (this.debug) {
      const timestamp = new Date().toISOString();
      if (data !== undefined) {
        // eslint-disable-next-line no-console
        console.log(`[ProductTour ${timestamp}] ${message}`, data);
      } else {
        // eslint-disable-next-line no-console
        console.log(`[ProductTour ${timestamp}] ${message}`);
      }
    }
  }

  initialize(token: string, debug = false): void {
    this.debug = debug;
    this.log('Initializing product tour service', { tokenProvided: !!token });

    if (this.initialized) {
      this.log('Service already initialized, skipping');
      return;
    }

    if (!token) {
      this.log('No token provided, service will not initialize');
      return;
    }

    try {
      loadUsetifulScript(token, { identifyUser: true });
      this.initialized = true;
      this.log('Product tour service initialized successfully');
    } catch (error) {
      this.log('Failed to initialize product tour service', error);
      // eslint-disable-next-line no-console
      console.error('[ProductTour] Initialization failed:', error);
      // Don't set initialized = true on failure
    }
  }

  identifyUser(userId: string, userProperties?: Record<string, unknown>): void {
    this.log('identifyUser called', { userId, userProperties });

    if (!this.initialized) {
      this.log('Service not initialized, cannot identify user');
      return;
    }

    try {
      // Use the SDK's exported function for setting user tags
      setUsetifulTags({
        userId,
        ...userProperties,
      });
      this.log('User identified successfully', { userId });
    } catch (error) {
      this.log('Failed to identify user', error);
      // eslint-disable-next-line no-console
      console.error('[ProductTour] User identification failed:', error);
    }
  }

  addTags(tags: Record<string, unknown>): void {
    this.log('addTags called', { tags });

    if (!this.initialized) {
      this.log('Service not initialized, cannot add tags');
      return;
    }

    try {
      // Append additional tags to existing user properties
      setUsetifulTags(tags);
      this.log('Tags added successfully', {
        tagCount: Object.keys(tags).length,
      });
    } catch (error) {
      this.log('Failed to add tags', error);
      // eslint-disable-next-line no-console
      console.error('[ProductTour] Adding tags failed:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}

// Global product tour service instance
let productTourInstance: IProductTourService = new UsetifulProductTourService();

/**
 * The proxy ensures that all consumers always get the latest product tour instance,
 * even if they import productTour before initProductTour is called.
 *
 * The proxy also ensures proper function binding, so methods can be destructured safely:
 * const { addTags } = productTour; // This works correctly
 */
export const productTour: IProductTourService = new Proxy(
  {} as IProductTourService,
  {
    get(_target, prop) {
      const value = productTourInstance[prop as keyof IProductTourService];

      // If the property is a function, bind it to the current productTourInstance
      // to preserve the correct 'this' context
      if (typeof value === 'function') {
        return value.bind(productTourInstance);
      }

      return value;
    },
  },
);

/**
 * Initialize product tour with runtime config.
 *
 * This function sets up the product tour service at runtime.
 *
 * Example usage:
 *   initProductTour({
 *     token: 'your-token',
 *     debug: true,
 *   });
 *
 * @param config Product tour configuration object.
 * @returns Product tour instance.
 */
export function initProductTour(config: {
  token: string;
  debug?: boolean;
}): IProductTourService {
  const { token, debug = false } = config;

  // Only initialize if token is provided
  if (!token || token === '') {
    if (debug) {
      // eslint-disable-next-line no-console
      console.log(
        '[ProductTour] No token provided, service will not initialize',
      );
    }
    return productTourInstance;
  }

  productTourInstance = new UsetifulProductTourService();
  productTourInstance.initialize(token, debug);
  return productTourInstance;
}
